<template>
  <section id="empresas" class="empresas-section">
    <!-- Fondos y destellos cian tecnológicos -->
    <div class="empresas-bg" aria-hidden="true">
      <div class="bg-glow glow-left"></div>
      <div class="bg-glow glow-right"></div>
      <div class="grid-overlay"></div>
    </div>

    <div class="empresas-container">
      <!-- Encabezado compacto y visible siempre -->
      <div class="empresas-header">
        <div class="header-badge">
          <span class="badge-dot"></span>
          <span>CLIENTES CORPORATIVOS</span>
        </div>
        <h2 class="empresas-title">
          Empresas que Confían en <span class="text-gradient">Nuestros Servicios</span>
        </h2>
        <p class="empresas-subtitle">
          Soporte técnico, infraestructura y mantenimiento garantizado para empresas aliadas.
        </p>
      </div>

      <!-- Carrusel Infinito en Movimiento Continuo (Giro Automático) -->
      <div class="carousel-container" v-if="displayEmpresas.length > 0">
        <!-- Flecha Anterior (retroceso manual interactivo) -->
        <button 
          class="carousel-nav-btn prev" 
          @click="prevSlide" 
          aria-label="Empresa anterior"
          title="Ver anteriores"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <!-- Ventana del Carrusel con Desplazamiento Infinito -->
        <div 
          class="carousel-window" 
          ref="carouselWindow"
          @mouseenter="pauseAutoScroll"
          @mouseleave="resumeAutoScroll"
          @touchstart="pauseAutoScroll"
          @touchend="resumeAutoScroll"
        >
          <div class="carousel-track" ref="carouselTrack">
            <div
              v-for="(empresa, index) in displayEmpresas"
              :key="`${empresa.id || index}-${index}`"
              class="empresa-card-wrapper"
            >
              <div class="empresa-card">
                <div class="card-glow"></div>
                <div class="card-circuit-accent"></div>
                
                <!-- Icono Corporativo -->
                <div class="empresa-icon-box">
                  <div class="icon-pulse-ring"></div>
                  <i class="fas fa-building empresa-icon"></i>
                </div>

                <!-- Datos de la Empresa (Solo Nombre) -->
                <div class="empresa-info">
                  <h3 class="empresa-nombre" :title="empresa.nombre">
                    {{ empresa.nombre }}
                  </h3>
                </div>

                <!-- Tag de estado corporativo -->
                <div class="card-footer-badge">
                  <span class="status-pulse"></span>
                  <span>Cliente Corporativo</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Flecha Siguiente (avance manual interactivo) -->
        <button 
          class="carousel-nav-btn next" 
          @click="nextSlide" 
          aria-label="Empresa siguiente"
          title="Ver siguientes"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { obtenerEmpresasClientes } from '@/api/clientes.js';

export default {
  name: 'ServiceCarousel',
  data() {
    return {
      empresas: [],
      cardsPerView: 6,
      loading: true,
      isPaused: false,
      rafId: null,
      scrollSpeed: 0.8, // Pixeles de desplazamiento suave por frame
      resumeTimeout: null
    };
  },
  computed: {
    // Genera una lista continua y duplicada para el bucle infinito perfecto (seamless loop)
    displayEmpresas() {
      if (!this.empresas || this.empresas.length === 0) return [];
      let base = [...this.empresas];
      // Nos aseguramos de tener al menos 12 elementos base para cubrir cualquier resolución de pantalla
      while (base.length < 12) {
        base = [...base, ...this.empresas];
      }
      // Duplicamos exactamente la base: la primera mitad se reproduce y al llegar a la segunda se reinicia de forma invisible
      return [...base, ...base];
    }
  },
  async mounted() {
    this.updateCardsPerView();
    window.addEventListener('resize', this.updateCardsPerView);
    await this.cargarEmpresas();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCardsPerView);
    this.stopAutoScroll();
    if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
  },
  methods: {
    async cargarEmpresas() {
      try {
        this.loading = true;
        const res = await obtenerEmpresasClientes();
        if (res && res.data && res.data.length > 0) {
          this.empresas = res.data;
        }
      } catch (err) {
        console.error('Error cargando empresas:', err);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.startAutoScroll();
        });
      }
    },
    updateCardsPerView() {
      const width = window.innerWidth;
      if (width < 480) {
        this.cardsPerView = 2;
      } else if (width < 768) {
        this.cardsPerView = 3;
      } else if (width < 1024) {
        this.cardsPerView = 4;
      } else {
        this.cardsPerView = 6;
      }
    },
    startAutoScroll() {
      this.stopAutoScroll();
      const step = () => {
        const el = this.$refs.carouselWindow;
        if (el && !this.isPaused) {
          const halfWidth = el.scrollWidth / 2;
          if (halfWidth > 0) {
            el.scrollLeft += this.scrollSpeed;
            // Cuando supera la mitad (primer set completo), se reinicia al inicio de forma instantánea e imperceptible
            if (el.scrollLeft >= halfWidth) {
              el.scrollLeft -= halfWidth;
            }
          }
        }
        this.rafId = requestAnimationFrame(step);
      };
      this.rafId = requestAnimationFrame(step);
    },
    stopAutoScroll() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    pauseAutoScroll() {
      this.isPaused = true;
    },
    resumeAutoScroll() {
      this.isPaused = false;
    },
    temporarilyPause() {
      this.pauseAutoScroll();
      if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.resumeAutoScroll();
      }, 1800);
    },
    nextSlide() {
      const el = this.$refs.carouselWindow;
      if (!el) return;
      this.temporarilyPause();
      const cardWidth = el.clientWidth / this.cardsPerView;
      const scrollStep = cardWidth * 2; // Desplaza 2 tarjetas hacia adelante
      const halfWidth = el.scrollWidth / 2;

      if (el.scrollLeft + scrollStep >= halfWidth) {
        el.scrollLeft -= halfWidth;
      }
      el.scrollBy({ left: scrollStep, behavior: 'smooth' });
    },
    prevSlide() {
      const el = this.$refs.carouselWindow;
      if (!el) return;
      this.temporarilyPause();
      const cardWidth = el.clientWidth / this.cardsPerView;
      const scrollStep = cardWidth * 2; // Desplaza 2 tarjetas hacia atrás
      const halfWidth = el.scrollWidth / 2;

      if (el.scrollLeft - scrollStep < 0) {
        el.scrollLeft += halfWidth;
      }
      el.scrollBy({ left: -scrollStep, behavior: 'smooth' });
    }
  }
};
</script>

<style src="@/css/service-carousel/service-carousel.css"></style>
